import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlay,
    faPause,
    faVolumeUp,
    faHistory,
    faVolumeDown,
    faVolumeMute
} from '@fortawesome/free-solid-svg-icons';

const SquareContainer = styled.div`
    position: relative;
    aspect-ratio: 1;
    height: ${props => props.dimensions?.width || '300px'};
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const AlbumArtwork = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${props => props.dimensions?.borderRadius || '8px'};
    z-index: 1;

    &:after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%);
        z-index: 2;
    }

    img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        z-index: 1;
    }
`;

const StationInfo = styled.div`
    padding: 20px;
    z-index: 3;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const RadioName = styled.div`
    font-size: ${props => `${props.fontSize.value}${props.fontSize.unit}`};
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    color: ${props => props.colors?.text || 'white'};
`;

const LiveContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    background: rgba(0, 0, 0, 0.3);
    padding: 4px 8px;
    border-radius: 12px;
    backdrop-filter: blur(4px);
`;

const LiveIndicator = styled.div`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.colors?.liveIndicator || '#FF0000'};
    animation: pulse 2s infinite;
`;

const LiveText = styled.div`
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    color: ${props => props.colors?.text || 'white'};
    font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}`};
`;

const PlayerContent = styled.div`
    position: relative;
    z-index: 3;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: auto;
`;

const SongInfo = styled.div`
    padding: 0;
    margin-bottom: 20px;
    text-align: ${props => props.textAlign};
`;

const SongName = styled.div`
    font-size: ${props => `${props.fontSize.value}${props.fontSize.unit}`};
    font-weight: bold;
    margin-bottom: 5px;
    color: ${props => props.colors?.text || 'white'};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const ArtistName = styled.div`
    font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}`};
    opacity: 0.8;
    color: ${props => props.colors?.text || 'white'};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const Controls = styled.div`
    display: flex;
    align-items: center;
    justify-content: ${props => {
        if (props.buttonAlign === 'left') return 'flex-start';
        if (props.buttonAlign === 'right') return 'flex-end';
        return 'center';
    }};
    gap: 30px;
    padding: 0;
    margin-bottom: 30px;
`;

const ControlButton = styled.button`
    background: ${props => (props.buttonStyle === 'text' || props.buttonStyle === 'icons') ? 'transparent' : props.colors?.buttonBackground || 'none'};
    border: none;
    color: ${props => props.colors?.icon || 'white'};
    cursor: pointer;
    padding: ${props => (props.buttonStyle === 'text' || props.buttonStyle === 'icons') ? '0.25rem' : '0.5rem'};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${props => props.isPlay ? '1.5rem' : '1rem'};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    border-radius: ${props => {
        switch (props.buttonStyle) {
            case 'circle': return '50%';
            case 'square': return '0';
            case 'rounded': return '4px';
            case 'text':
            case 'icons':
                return '0';
            default: return '50%';
        }
    }};
    width: ${props => {
        if (props.buttonStyle === 'text' || props.buttonStyle === 'icons') return 'auto';
        const baseSize = 36;
        return props.isPlay ? `${baseSize * 1.3}px` : `${baseSize}px`;
    }};
    height: ${props => {
        if (props.buttonStyle === 'text' || props.buttonStyle === 'icons') return 'auto';
        const baseSize = 36;
        return props.isPlay ? `${baseSize * 1.3}px` : `${baseSize}px`;
    }};
    aspect-ratio: ${props => (props.buttonStyle === 'circle' || props.buttonStyle === 'square') ? '1' : 'auto'};
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.8;
        color: ${props => props.colors?.accent || props.colors?.icon || 'white'};
        transform: ${props => props.isPlay ? 'scale(1.05)' : 'scale(1.02)'};
    }
`;

const TimeBarContainer = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0;
    width: 100%;
    z-index: 3;
`;

const TimeBar = styled.div`
    width: 100%;
    height: 4px;
    background: ${props => props.colors?.timeBar ? `${props.colors.timeBar}33` : 'transparent'};
    border-radius: 0;
    overflow: hidden;
    position: relative;
`;

const TimeBarProgress = styled.div`
    height: 100%;
    background: ${props => props.colors?.timeBar || '#333333'};
    width: ${props => props.progress}%;
    border-radius: 0;
`;

const TimeInfo = styled.div`
    position: absolute;
    bottom: 4px;
    left: 0;
    width: 100%;
    padding: 0 10px;
    z-index: 4;
    display: flex;
    justify-content: space-between;
    font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}`};
    color: ${props => props.colors?.text || 'white'};
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
`;

const SquareLayout = ({
    colors,
    dimensions,
    settings,
    textAlign,
    buttonAlign,
    albumArt,
    fontSize,
    buttonStyle,
    currentSong,
    isPlaying,
    elapsed,
    timeProgress,
    controls,
    volume,
    handleVolumeChange,
    showVolumeBar,
    setShowVolumeBar,
    showHistoryModal,
    setShowHistoryModal,
    getVolumeIcon
}) => {
    return (
        <SquareContainer dimensions={dimensions}>
            <AlbumArtwork settings={{ albumArt }} dimensions={dimensions}>
                {albumArt !== 'disable' && (
                    currentSong?.artwork ? (
                        <img
                            src={currentSong.artwork}
                            alt="Album Art"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: albumArt || 'cover'
                            }}
                        />
                    ) : (
                        'ALBUM ARTWORK AREA'
                    )
                )}
            </AlbumArtwork>

            <StationInfo>
                {settings.radioName && currentSong?.radioName && (
                    <RadioName colors={colors} fontSize={fontSize}>
                        {currentSong.radioName}
                    </RadioName>
                )}
                {settings.liveIndicator && currentSong?.isLive !== undefined && (
                    <LiveContainer>
                        <LiveIndicator colors={colors} />
                        <LiveText colors={colors} fontSize={fontSize}>
                            {currentSong.streamerName || 'LIVE'}
                        </LiveText>
                    </LiveContainer>
                )}
            </StationInfo>

            <PlayerContent>
                {(settings.songName || settings.artistName) && currentSong && (
                    <SongInfo textAlign={textAlign}>
                        {settings.songName && (
                            <SongName colors={colors} fontSize={fontSize}>
                                {currentSong.title}
                            </SongName>
                        )}
                        {settings.artistName && (
                            <ArtistName colors={colors} fontSize={fontSize}>
                                {currentSong.artist}
                            </ArtistName>
                        )}
                    </SongInfo>
                )}

                <Controls buttonAlign={buttonAlign}>
                    {settings.songHistory && (
                        <ControlButton
                            colors={colors}
                            buttonStyle={buttonStyle}
                            onClick={() => setShowHistoryModal(true)}
                        >
                            {buttonStyle === 'text' ? (
                                'History'
                            ) : (
                                <FontAwesomeIcon icon={faHistory} />
                            )}
                        </ControlButton>
                    )}
                    <ControlButton
                        colors={colors}
                        isPlay
                        buttonStyle={buttonStyle}
                        onClick={controls.togglePlay}
                    >
                        {buttonStyle === 'text' ? (
                            isPlaying ? 'Pause' : 'Play'
                        ) : (
                            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                        )}
                    </ControlButton>
                    {settings.volumeControl && (
                        <ControlButton
                            colors={colors}
                            buttonStyle={buttonStyle}
                            onClick={() => setShowVolumeBar(!showVolumeBar)}
                        >
                            {buttonStyle === 'text' ? (
                                'Volume'
                            ) : (
                                <FontAwesomeIcon icon={getVolumeIcon()} />
                            )}
                        </ControlButton>
                    )}
                </Controls>

                {settings.timeBar && (
                    <TimeBarContainer>
                        <TimeBar colors={colors}>
                            <TimeBarProgress
                                colors={colors}
                                progress={timeProgress}
                            />
                        </TimeBar>
                    </TimeBarContainer>
                )}

                {settings.time && currentSong && (
                    <TimeInfo colors={colors} fontSize={fontSize}>
                        <span>{formatTime(elapsed)}</span>
                        <span>{formatTime(currentSong.duration)}</span>
                    </TimeInfo>
                )}
            </PlayerContent>

            {settings.songHistory && (
                <div class="history-modal" style="display: none;">
                    <div class="history-modal-overlay"></div>
                    <div class="history-modal-content">
                        <h3 class="history-modal-title">${settings.historyHeaderText || 'Song History'}</h3>
                        <button class="history-modal-close">
                            <i class="fas fa-times"></i>
                        </button>
                        <div class="history-list"></div>
                    </div>
                </div>
            )}
        </SquareContainer>
    );
};

const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

export default SquareLayout;

export const generateSquareLayout = ({ settings, albumArt, buttonStyle }) => `
<div id="radio-player" class="radio-player square">
    ${albumArt !== 'disable' ? `
    <div class="album-artwork">
        <img class="artwork-image" src="" alt="Album Art" style="display: none;" />
        <div class="artwork-placeholder">ALBUM ARTWORK AREA</div>
        <div class="artwork-overlay"></div>
    </div>
    ` : ''}

    <div class="player-content">
        <div class="station-info">
            ${settings?.radioName ? '<div class="radio-name"></div>' : ''}
            ${settings?.liveIndicator ? `
            <div class="live-container">
                <div class="live-indicator"></div>
                <div class="live-text">LIVE</div>
            </div>
            ` : ''}
        </div>

        ${(settings?.songName || settings?.artistName) ? `
        <div class="song-info">
            ${settings?.songName ? '<div class="song-name"></div>' : ''}
            ${settings?.artistName ? '<div class="artist-name"></div>' : ''}
        </div>
        ` : ''}

        <div class="controls-container">
            <div class="controls">
                ${settings?.songHistory ? `
                <button class="control-button history-button">
                    ${buttonStyle === 'text' ? 'History' : '<i class="fas fa-history"></i>'}
                </button>
                ` : ''}
                <button class="control-button play-button">
                    ${buttonStyle === 'text' ? 'Play' : '<i class="fas fa-play"></i>'}
                </button>
                ${settings?.volumeControl ? `
                <button class="control-button volume-button">
                    ${buttonStyle === 'text' ? 'Volume' : '<i class="fas fa-volume-up"></i>'}
                </button>
                <div class="volume-bar-container">
                    <input type="range" class="volume-slider" min="0" max="100" value="100">
                </div>
                ` : ''}
            </div>
        </div>

        ${settings?.time ? `
        <div class="time-info">
            <span class="elapsed-time">0:00</span>
            <span class="duration-time">0:00</span>
        </div>
        ` : ''}

        ${settings?.timeBar ? `
        <div class="time-bar-container">
            <div class="time-bar">
                <div class="time-bar-progress"></div>
            </div>
        </div>
        ` : ''}
    </div>

    <!-- History Modal -->
    ${settings?.songHistory ? `
    <div class="history-modal" style="display: none;">
        <div class="history-modal-overlay"></div>
        <div class="history-modal-content">
            <h3 class="history-modal-title">${settings.historyHeaderText || 'Song History'}</h3>
            <button class="history-modal-close">
                <i class="fas fa-times"></i>
            </button>
            <div class="history-list"></div>
        </div>
    </div>
    ` : ''}
</div>`; 